import { z } from 'zod';
import { richTextDocWireSchema } from '../richtext/schema';
import {
  milestoneStatusSchema,
  projectMemberRoleSchema,
  projectStatusSchema,
  projectVisibilitySchema,
} from '../tasks';
import {
  commaList,
  isoDateTimeSchema,
  localDateSchema,
  singleLineText,
  uuidSchema,
} from './common';
import { pageInfoSchema, paginated, paginationQuerySchema } from './pagination';
import {
  BOARD_LAYOUTS,
  TASK_SORTS,
  kanbanColumnSchema,
  taskDtoSchema,
  taskFiltersSchema,
} from './tasks';

/**
 * Projects and milestones (SPEC §6.6 "Projects: lightweight grouping with milestones, progress %,
 * list and board views"; PLAN §3.2 `projects`, `project_members`, `project_milestones`, §4).
 * Owned by the Phase 4 projects workstream (`apps/api/src/modules/projects`).
 *
 * Progress is computed on read (PLAN §3.2): `floor(100 × closed ÷ total)` where closed = done +
 * verified and total excludes draft, declined and cancelled tasks; null = "no tasks yet". Project
 * visibility: `workspace` projects are visible to every non-guest member, `members` projects to
 * their members; the tasks inside still follow task visibility (D-037).
 */

export const projectIdParamsSchema = z.object({ projectId: uuidSchema });
export type ProjectIdParams = z.infer<typeof projectIdParamsSchema>;
export const projectMemberParamsSchema = z.object({ projectId: uuidSchema, userId: uuidSchema });
export const milestoneIdParamsSchema = z.object({ milestoneId: uuidSchema });

export const projectNameSchema = singleLineText(1, 120);

export const projectTaskCountsDtoSchema = z.object({
  /** Proposed … verified (draft, declined and cancelled excluded). */
  total: z.int().nonnegative(),
  /** Done + verified. */
  closed: z.int().nonnegative(),
  open: z.int().nonnegative(),
  /** Active tasks past their deadline. */
  overdue: z.int().nonnegative(),
  /** The Slipping rule (`slip_count ≥ 2` or overdue > 3 days). */
  slipping: z.int().nonnegative(),
});
export type ProjectTaskCountsDto = z.infer<typeof projectTaskCountsDtoSchema>;

export const projectDtoSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: richTextDocWireSchema.nullable(),
  descriptionText: z.string().nullable(),
  departmentId: uuidSchema.nullable(),
  leadUserId: uuidSchema,
  status: projectStatusSchema,
  visibility: projectVisibilitySchema,
  startDate: localDateSchema.nullable(),
  targetDate: localDateSchema.nullable(),
  linkedConversationId: uuidSchema.nullable(),
  completedAt: isoDateTimeSchema.nullable(),
  createdByUserId: uuidSchema,
  /** `project_members` (the lead included); for `workspace` projects the explicit members only. */
  members: z.array(z.object({ userId: uuidSchema, role: projectMemberRoleSchema })),
  /** Null = no tasks yet. */
  progressPct: z.int().min(0).max(100).nullable(),
  counts: projectTaskCountsDtoSchema,
  /** The viewer may edit (lead or admin+). */
  canEdit: z.boolean(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  version: z.int().positive(),
});
export type ProjectDto = z.infer<typeof projectDtoSchema>;

export const projectResponseSchema = z.object({ project: projectDtoSchema });

export const listProjectsQuerySchema = paginationQuerySchema.extend({
  status: commaList(projectStatusSchema).optional(),
  departmentId: uuidSchema.optional(),
  leadUserId: uuidSchema.optional(),
  /** Only projects I am a member or the lead of. */
  mine: z.enum(['true', 'false', '1', '0']).optional(),
  q: singleLineText(1, 120).optional(),
});
export const projectListResponseSchema = paginated(projectDtoSchema);

export const createProjectRequestSchema = z.strictObject({
  name: projectNameSchema,
  description: richTextDocWireSchema.nullable().optional(),
  departmentId: uuidSchema.nullable().optional(),
  leadUserId: uuidSchema,
  startDate: localDateSchema.nullable().optional(),
  targetDate: localDateSchema.nullable().optional(),
  visibility: projectVisibilitySchema.default('workspace'),
  linkedConversationId: uuidSchema.nullable().optional(),
  /** Extra members (role `member`); the lead is added as `lead`. */
  memberIds: z.array(uuidSchema).max(200).optional(),
});
export type CreateProjectRequest = z.input<typeof createProjectRequestSchema>;

export const updateProjectRequestSchema = z
  .strictObject({
    name: projectNameSchema.optional(),
    description: richTextDocWireSchema.nullable().optional(),
    departmentId: uuidSchema.nullable().optional(),
    leadUserId: uuidSchema.optional(),
    status: projectStatusSchema.optional(),
    visibility: projectVisibilitySchema.optional(),
    startDate: localDateSchema.nullable().optional(),
    targetDate: localDateSchema.nullable().optional(),
    linkedConversationId: uuidSchema.nullable().optional(),
  })
  .refine((body) => Object.keys(body).length > 0, 'Change at least one field');
export type UpdateProjectRequest = z.infer<typeof updateProjectRequestSchema>;

export const putProjectMemberRequestSchema = z.strictObject({
  role: z.enum(['member', 'viewer']).default('member'),
});

export const milestoneDtoSchema = z.object({
  id: uuidSchema,
  projectId: uuidSchema,
  name: z.string(),
  dueDate: localDateSchema.nullable(),
  position: z.int().nonnegative(),
  status: milestoneStatusSchema,
  completedAt: isoDateTimeSchema.nullable(),
  progressPct: z.int().min(0).max(100).nullable(),
  counts: projectTaskCountsDtoSchema,
  /** Every counted task is closed (the lead marks the milestone done; it is not automatic). */
  allTasksDone: z.boolean(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type MilestoneDto = z.infer<typeof milestoneDtoSchema>;

export const milestoneListResponseSchema = z.object({ items: z.array(milestoneDtoSchema) });
export const milestoneResponseSchema = z.object({ milestone: milestoneDtoSchema });
export const createMilestoneRequestSchema = z.strictObject({
  name: singleLineText(1, 120),
  dueDate: localDateSchema.nullable().optional(),
  position: z.int().min(0).optional(),
});
export const updateMilestoneRequestSchema = z
  .strictObject({
    name: singleLineText(1, 120).optional(),
    dueDate: localDateSchema.nullable().optional(),
    position: z.int().min(0).optional(),
    status: milestoneStatusSchema.optional(),
  })
  .refine((body) => Object.keys(body).length > 0, 'Change at least one field');

/** `GET /projects/:projectId/tasks`: the project's tasks, each filtered by task visibility. */
export const projectTasksQuerySchema = paginationQuerySchema
  .extend(taskFiltersSchema.shape)
  .extend({
    layout: z.enum(BOARD_LAYOUTS).default('list'),
    sort: z.enum(TASK_SORTS).default('due'),
  });
export const projectTasksResponseSchema = z.discriminatedUnion('layout', [
  z.object({
    layout: z.literal('list'),
    items: z.array(taskDtoSchema),
    nextCursor: z.string().nullable(),
    page: pageInfoSchema.optional(),
  }),
  z.object({ layout: z.literal('kanban'), columns: z.array(kanbanColumnSchema) }),
]);
export type ProjectTasksResponse = z.infer<typeof projectTasksResponseSchema>;
